import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard,
  Clock,
  CheckCircle,
  AlertCircle,
  Shield,
  X,
  AlertTriangle,
  Home,
  Building,
  Building2,
  Plus,
  Tag,
  Percent,
  Settings,
  RefreshCw,
  MapPin
} from 'lucide-react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const SubscriptionDashboard = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [voucherCode, setVoucherCode] = useState({});
  const [applyingVoucher, setApplyingVoucher] = useState({});
  const [voucherApplied, setVoucherApplied] = useState({});
  const [completingPayment, setCompletingPayment] = useState(false);

  const forceRefresh = () => {
    console.log('🔄 Force refreshing subscription data...');
    setSubscriptions([]);
    setMemberships([]);
    setLoading(true);
    
    // Clear any potential cached state and re-fetch
    setTimeout(() => {
      fetchSubscriptions();
      fetchMemberships();
    }, 100);
  };

  const fetchMemberships = useCallback(async () => {
    try {
      console.log('🔄 Fetching memberships...');
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('❌ No token found for memberships');
        return;
      }

      console.log('📡 Making API call to /membership/my-membership');
      const response = await api.get('/membership/my-membership');
      console.log('📬 Membership API response:', response);
      
      if (response?.success) {
        // Convert single membership to array format for consistent handling
        const membershipArray = response.membership ? [response.membership] : [];
        console.log('✅ Setting memberships:', membershipArray);
        setMemberships(membershipArray);
      }
    } catch (error) {
      console.error('❌ Error fetching membership data:', error);
      // Don't show error toast for membership failures since subscriptions might still work
    }
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      console.log('🔄 Fetching subscriptions...');
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('❌ No token found for subscriptions');
        toast.error('Please log in to view your subscriptions');
        setLoading(false);
        return;
      }

      console.log('📡 Making API call to /subscriptions/my-subscriptions');
      const response = await api.get('/subscriptions/my-subscriptions');
      console.log('📬 Subscriptions API response:', response);

      if (response?.success) {
        console.log('✅ Setting subscriptions:', response.subscriptions || []);
        setSubscriptions(response.subscriptions || []);
        
        // If no subscriptions found but we had some in state, clear them
        if ((!response.subscriptions || response.subscriptions.length === 0) && subscriptions.length > 0) {
          console.log('🧹 Clearing outdated subscription state');
          setSubscriptions([]);
        }
      } else {
        console.error('❌ Failed to fetch subscriptions:', response);
        toast.error(response.message || 'Failed to load subscriptions');
      }
    } catch (error) {
      console.error('❌ Error fetching subscriptions:', error);
      toast.error(error.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [subscriptions.length]);

  useEffect(() => {
    console.log('🚀 SubscriptionDashboard component mounted, fetching data...');
    
    // Handle payment activation when returning from payment gateway
    const handlePaymentActivation = async (reference, status) => {
      try {
        console.log('🔄 Attempting to activate subscription payment...', { reference, status });
        
        const response = await api.post('/subscriptions/activate-payment', {
          reference,
          status
        });

        if (response?.success) {
          console.log('✅ Subscription activated successfully');
          toast.success('Payment completed! Your subscription is now active.');
          
          // Clean up URL parameters
          const url = new URL(window.location);
          url.searchParams.delete('reference');
          url.searchParams.delete('status');
          window.history.replaceState({}, document.title, url.pathname);
          
          // Refresh subscription data
          setTimeout(() => {
            fetchSubscriptions();
            fetchMemberships();
          }, 1000);
        } else {
          console.log('⚠️ Subscription activation failed:', response?.message);
          toast.error(response?.message || 'Failed to activate subscription');
        }
      } catch (error) {
        console.error('❌ Error activating subscription:', error);
        toast.error('Failed to activate subscription after payment');
      }
    };
    
    // Check for payment completion in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference');
    const status = urlParams.get('status');
    
    if (reference && status === 'completed') {
      console.log('💳 Payment completion detected:', { reference, status });
      handlePaymentActivation(reference, status);
    }
    
    fetchSubscriptions();
    fetchMemberships();
  }, [fetchSubscriptions, fetchMemberships]);

  const cleanupCancelledSubscriptions = async () => {
    try {
      const response = await api.delete('/subscriptions/cleanup-cancelled');
      if (response?.success) {
        toast.success(response.message);
        fetchSubscriptions();
      } else {
        toast.error('Failed to cleanup subscriptions');
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Failed to cleanup subscriptions');
    }
  };

  const handleCancelSubscription = async (immediate = false) => {
    if (!selectedSubscription) return;

    if (selectedSubscription.status === 'CANCELLED' || selectedSubscription.status === 'EXPIRED') {
      const errorMsg = selectedSubscription.status === 'EXPIRED'
        ? 'Subscription has already expired'
        : 'Subscription is already cancelled';
      toast.error(errorMsg);
      setShowCancelModal(false);
      return;
    }

    // Detect if this is a membership or subscription
    const isMembership = selectedSubscription.tierType !== undefined; // Memberships have tierType, subscriptions have propertyType
    const itemType = isMembership ? 'membership' : 'subscription';

    // Different confirmation messages for pending vs active subscriptions/memberships
    let confirmText;
    if (selectedSubscription.status === 'PENDING') {
      confirmText = `Are you sure you want to cancel this pending ${itemType}? It will be removed from your account.`;
    } else if (immediate) {
      confirmText = `Are you sure you want to cancel this ${itemType} immediately? You will lose access right away and no refund will be provided.`;
    } else {
      confirmText = `Are you sure you want to cancel this ${itemType}? It will remain active until the end of your current billing period.`;
    }

    if (!window.confirm(confirmText)) {
      return;
    }

    setCancelLoading(true);
    try {
      // Use different API endpoints for membership vs subscription
      const endpoint = isMembership 
        ? `/membership/cancel`
        : `/subscriptions/${selectedSubscription._id}/cancel`;
      
      const payload = isMembership 
        ? { immediate } // Membership API doesn't need ID in URL
        : { immediate };

      const response = await api.put(endpoint, payload);

      if (response?.success) {
        // Handle pending subscription deletion
        if (response.deleted) {
          toast.success(`Pending ${itemType} removed successfully`);
        } else {
          const message = immediate
            ? `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} cancelled immediately - access ended`
            : `Auto-renewal stopped - access continues until end of period`;
          toast.success(message);
        }
        setShowCancelModal(false);
        fetchSubscriptions(); // Refresh the subscriptions list
        fetchMemberships(); // Refresh the memberships list too
      } else {
        toast.error(response.message || `Failed to cancel ${itemType}`);
      }
    } catch (error) {
      console.error('❌ Cancellation failed:', error);
      
      // If subscription/membership not found (404), remove it from local state
      if (error.message?.includes('not found') || error.message?.includes('Subscription not found') || error.status === 404) {
        const isMembership = selectedSubscription.tierType !== undefined;
        
        if (isMembership) {
          // Remove from memberships state
          setMemberships(prev => prev.filter(m => m._id !== selectedSubscription._id));
          toast.success('Outdated membership removed from your account');
        } else {
          // Remove from subscriptions state
          setSubscriptions(prev => prev.filter(s => s._id !== selectedSubscription._id));
          toast.success('Outdated subscription removed from your account');
        }
        
        setShowCancelModal(false);
        return;
      }
      
      toast.error(error.message || `Failed to cancel ${itemType}`);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleChangePlan = async (newPropertyType) => {
    if (!selectedSubscription) return;

    setChangingPlan(true);
    try {
      const response = await api.post('/subscriptions/change-plan', {
        subscriptionId: selectedSubscription._id,
        newPropertyType,
        billingCycle: 'MONTHLY',
        immediate: true
      });

      if (response?.success && response?.paymentUrl) {
        window.location.href = response.paymentUrl;
      } else {
        toast.error(response?.message || 'Failed to initiate plan change');
      }
    } catch (error) {
      console.error('Plan change failed:', error);
      toast.error(error.message || 'Failed to initiate plan change');
    } finally {
      setChangingPlan(false);
    }
  };

  const handleApplyVoucher = async (subscriptionId) => {
    const code = voucherCode[subscriptionId];
    if (!code || !code.trim()) {
      toast.error('Please enter a voucher code');
      return;
    }

    setApplyingVoucher({ ...applyingVoucher, [subscriptionId]: true });
    try {
      const response = await api.post(`/subscriptions/${subscriptionId}/apply-voucher`, {
        voucherCode: code.trim().toUpperCase()
      });

      if (response?.success) {
        toast.success(
          `Voucher applied! Saved $${response.discount.toFixed(2)}`,
          { duration: 4000, icon: '🎉' }
        );
        setVoucherApplied({ ...voucherApplied, [subscriptionId]: true });
        // Refresh subscriptions to get updated price
        await fetchSubscriptions();
      } else {
        toast.error(response?.message || 'Failed to apply voucher');
      }
    } catch (error) {
      console.error('Voucher application error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to apply voucher');
    } finally {
      setApplyingVoucher({ ...applyingVoucher, [subscriptionId]: false });
    }
  };

  const handleRemoveVoucher = async (subscriptionId) => {
    setApplyingVoucher({ ...applyingVoucher, [subscriptionId]: true });
    try {
      const response = await api.post(`/subscriptions/${subscriptionId}/remove-voucher`);

      if (response?.success) {
        toast.success('Voucher removed');
        setVoucherApplied({ ...voucherApplied, [subscriptionId]: false });
        setVoucherCode({ ...voucherCode, [subscriptionId]: '' });
        // Refresh subscriptions to get updated price
        await fetchSubscriptions();
      } else {
        toast.error(response?.message || 'Failed to remove voucher');
      }
    } catch (error) {
      console.error('Voucher removal error:', error);
      toast.error(error.message || 'Failed to remove voucher');
    } finally {
      setApplyingVoucher({ ...applyingVoucher, [subscriptionId]: false });
    }
  };

  // Subscription payment retry functionality
  const handleRetrySubscriptionPayment = async (subscription) => {
    if (!subscription || subscription.status !== 'PENDING') {
      toast.error('No pending payment found for this subscription');
      return;
    }

    setCompletingPayment(true);
    try {
      console.log('🔄 Attempting to retry payment for pending subscription...');
      
      // Check if user is logged in
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to complete your payment');
        setCompletingPayment(false);
        return;
      }

      // Call API to retry payment for the pending subscription
      const response = await api.post('/subscriptions/retry-payment', {});

      console.log('📋 Subscription Retry Payment API Response:', response);

      if (response?.success) {
        const redirectUrl = response.paymentUrl;
        console.log('✅ Subscription retry payment successful:', {
          redirectUrl,
          paymentUrl: response.paymentUrl
        });
        
        if (redirectUrl) {
          // Show success message and redirect to payment gateway
          toast.success('Redirecting to payment gateway to complete your subscription...', { 
            duration: 2000 
          });
          
          // Small delay for better UX, then redirect to HitPay
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1500);
        } else {
          toast.error('Payment URL not available. Please try again.');
        }
      } else {
        toast.error(response?.message || 'Failed to initiate payment retry');
      }
    } catch (error) {
      console.error('❌ Subscription payment retry failed:', error);
      toast.error(error.message || 'Failed to retry subscription payment');
    } finally {
      setCompletingPayment(false);
    }
  };

  // Membership payment retry functionality
  const handleCompletePayment = async (membership) => {
    if (!membership || membership.status !== 'PENDING') {
      toast.error('No pending payment found');
      return;
    }

    setCompletingPayment(true);
    try {
      console.log('🔄 Attempting to complete payment for pending membership...');
      
      // Check if user is logged in
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to complete your payment');
        setCompletingPayment(false);
        return;
      }

      // Call API to retry payment for the pending membership
      const response = await api.post('/membership/retry-payment', {});

      console.log('📋 Retry Payment API Response:', response);

      if (response?.success) {
        const redirectUrl = response.paymentUrl || response.checkoutUrl;
        console.log('✅ Retry payment successful:', {
          redirectUrl,
          paymentUrl: response.paymentUrl,
          checkoutUrl: response.checkoutUrl,
          autoActivated: response.autoActivated,
          demo: response.demo
        });
        
        if (redirectUrl) {
          // Show success message and redirect to payment gateway
          toast.success(response.demo ? 
            'Demo mode: Redirecting to demo checkout...' : 
            'Redirecting to payment gateway to complete your membership...', { 
            duration: 2000 
          });
          
          // Small delay for better UX, then redirect to HitPay
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1500);
        } else {
          // If no payment URL, membership might have been activated directly
          console.log('No payment URL returned, checking if membership was auto-activated');
          if (response.autoActivated) {
            toast.success('Payment completed successfully! Membership activated.');
            fetchMemberships(); // Refresh membership data
          } else {
            console.error('❌ No payment URL found in response:', response);
            toast.error('Payment URL not found. This could be due to:\n• Invalid subscription plan\n• HitPay service configuration issue\n• Missing payment gateway setup\n\nPlease contact support.');
          }
        }
      } else {
        console.error('❌ API returned unsuccessful response:', response);
        toast.error(response.message || 'Failed to initiate payment completion');
      }
    } catch (error) {
      console.error('❌ Complete payment failed:', error);
      
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        toast.error('Session expired. Please log in again.');
      } else if (error.message.includes('404')) {
        toast.error('Payment service not found. Please contact support.');
      } else if (error.message.includes('500')) {
        toast.error('Server error. Please try again later.');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        toast.error('Unable to connect to server. Please check your connection.');
      } else {
        toast.error(error.message || 'Failed to complete payment');
      }
    } finally {
      setCompletingPayment(false);
    }
  };

  const getPropertyIcon = (propertyType) => {
    switch (propertyType?.toUpperCase()) {
      case 'HDB':
        return Building;
      case 'CONDO':
        return Building2;
      case 'LANDED':
        return Home;
      default:
        return Building;
    }
  };

  const getStatusBadge = (subscription) => {
    if (subscription.status === 'ACTIVE') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-4 w-4 mr-1" />
          ACTIVE
        </span>
      );
    }

    if (subscription.status === 'CANCELLED' && subscription.hasActiveAccess) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
          <AlertTriangle className="h-4 w-4 mr-1" />
          CANCELLED
        </span>
      );
    }

    if (subscription.status === 'EXPIRED' || (subscription.status === 'CANCELLED' && !subscription.hasActiveAccess)) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <X className="h-4 w-4 mr-1" />
          EXPIRED
        </span>
      );
    }

    if (subscription.status === 'PENDING') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
          <Clock className="h-4 w-4 mr-1" />
          PENDING
        </span>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 pt-28">
        <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50">
      {/* Orange Banner Header - No Gap */}
      <div className="relative bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 text-white py-16 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20"></div>

        {/* Floating Elements */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/5 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white/10 rounded-full blur-lg"></div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6 border border-white/30">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-semibold">Subscription & Membership Management</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-white to-orange-100 bg-clip-text">
              My Subscriptions
            </h1>

            <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
              Manage your property maintenance subscriptions and memberships
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={forceRefresh}
                className="flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all border border-white/30"
                title="Refresh subscription data"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>

              <button
                onClick={cleanupCancelledSubscriptions}
                className="flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all border border-white/30"
                title="Clean up cancelled subscriptions"
              >
                <X className="h-4 w-4 mr-2" />
                Cleanup
              </button>
              <button
                onClick={() => navigate('/subscriptions')}
                className="flex items-center px-6 py-3 bg-white text-orange-600 rounded-lg hover:bg-orange-50 transition-all font-semibold shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Subscription
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">

      {/* Subscriptions List */}
      {(subscriptions.filter(sub => {
        // Hide CANCELLED subscriptions without active access
        if (sub.status === 'CANCELLED' && !sub.hasActiveAccess) return false;
        // Hide EXPIRED subscriptions
        if (sub.status === 'EXPIRED') return false;
        return true;
      }).length === 0 && memberships.length === 0) ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-12 text-center"
        >
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active Subscriptions or Memberships</h3>
          <p className="text-gray-600 mb-6">
            Subscribe to a maintenance plan to enjoy regular property checks and priority support.
          </p>
          <button
            onClick={() => navigate('/subscriptions')}
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors"
          >
            Browse Subscription Plans
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Display Memberships First */}
          {memberships.map((membership, index) => (
            <motion.div
              key={`membership-${membership._id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Membership Header */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="p-3 bg-white bg-opacity-20 rounded-lg mr-3">
                      <Building className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">
                        {membership.tier?.name} Property
                      </h3>
                      <p className="text-orange-100">
                        {membership.billingCycle} billing
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">
                      ${membership.billingCycle === 'YEARLY' 
                        ? membership.tier?.yearlyPrice 
                        : membership.tier?.monthlyPrice}.00
                    </div>
                    <div className="text-orange-100">
                      /{membership.billingCycle === 'YEARLY' ? 'year' : 'month'}
                    </div>
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className="flex justify-between items-center">
                  {membership.status === 'PENDING' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                      <Clock className="h-4 w-4 mr-1" />
                      PENDING
                    </span>
                  ) : membership.status === 'ACTIVE' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      ACTIVE
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                      <X className="h-4 w-4 mr-1" />
                      {membership.status}
                    </span>
                  )}
                </div>
              </div>

              {/* Membership Content */}
              <div className="p-6">
                {membership.status === 'PENDING' && (
                  <div className="mb-6">
                    <div className="flex items-center p-4 mb-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-orange-600 mr-3" />
                      <span className="text-orange-800">
                        Payment pending - Complete payment to activate
                      </span>
                    </div>
                    
                    <div className="space-y-4">
                      <button
                        onClick={() => handleCompletePayment(membership)}
                        disabled={completingPayment}
                        className="w-full bg-orange-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        <CreditCard className="h-5 w-5 mr-2" />
                        {completingPayment ? 'Processing...' : 'Make Payment'}
                      </button>

                      {/* Pending Membership Actions */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            setSelectedSubscription(membership);
                            setShowCancelModal(true);
                          }}
                          className="flex items-center px-4 py-2 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors text-sm"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel Membership
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {membership.status === 'ACTIVE' && (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                        <span className="text-green-800 font-medium">
                          Next billing: {membership.nextBillingDate ? 
                            new Date(membership.nextBillingDate).toLocaleDateString() : 
                            'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Membership Actions */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setSelectedSubscription(membership);
                          setShowCancelModal(true);
                        }}
                        className="flex items-center px-4 py-2 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors text-sm"
                      >
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Cancel Membership
                      </button>

                      <button
                        onClick={() => navigate('/membership')}
                        className="flex items-center px-4 py-2 border border-orange-300 rounded-lg text-orange-700 hover:bg-orange-50 transition-colors text-sm"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Manage
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}

          {/* Display Subscriptions */}
          {subscriptions
            .filter(sub => {
              // Hide CANCELLED subscriptions without active access
              if (sub.status === 'CANCELLED' && !sub.hasActiveAccess) {
                return false;
              }
              // Hide EXPIRED subscriptions
              if (sub.status === 'EXPIRED') {
                return false;
              }
              return true;
            })
            .map((subscription, index) => {
            const PropertyIcon = getPropertyIcon(subscription.propertyType);

            return (
              <motion.div
                key={subscription._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                {/* Subscription Header */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="p-3 bg-white bg-opacity-20 rounded-lg mr-3">
                        <PropertyIcon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">
                          {subscription.propertyType} Property
                        </h3>
                        <p className="text-orange-100 text-sm">
                          {subscription.billingCycle} billing
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(subscription)}
                  </div>

                  {/* Property Address Display */}
                  {subscription.property ? (
                    <div className="mb-4 bg-white bg-opacity-10 rounded-lg p-3">
                      <div className="flex items-start">
                        <MapPin className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0 text-orange-100" />
                        <div className="text-sm">
                          <div className="font-medium text-white">{subscription.property.name}</div>
                          {subscription.property.address ? (
                            <>
                              <div className="text-orange-100">{subscription.property.address.street}</div>
                              {subscription.property.address.unit && (
                                <div className="text-orange-100">Unit {subscription.property.address.unit}</div>
                              )}
                              <div className="text-orange-100">
                                {subscription.property.address.city || 'Singapore'} {subscription.property.address.postalCode}
                              </div>
                            </>
                          ) : subscription.property.fullAddress ? (
                            <div className="text-orange-100">{subscription.property.fullAddress}</div>
                          ) : (
                            <div className="text-orange-100">Property address not available</div>
                          )}
                        </div>
                      </div>
                      
                      {/* Property Details */}
                      {subscription.property.details && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {subscription.property.details.size && (
                            <span className="bg-white bg-opacity-20 text-orange-100 px-2 py-1 rounded text-xs">
                              {subscription.property.details.size} sq ft
                            </span>
                          )}
                          {subscription.property.details.rooms && (
                            <span className="bg-white bg-opacity-20 text-orange-100 px-2 py-1 rounded text-xs">
                              {subscription.property.details.rooms} rooms
                            </span>
                          )}
                          {subscription.property.details.bedrooms && (
                            <span className="bg-white bg-opacity-20 text-orange-100 px-2 py-1 rounded text-xs">
                              {subscription.property.details.bedrooms} bed
                            </span>
                          )}
                          {subscription.property.details.bathrooms && (
                            <span className="bg-white bg-opacity-20 text-orange-100 px-2 py-1 rounded text-xs">
                              {subscription.property.details.bathrooms} bath
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-4 bg-white bg-opacity-10 rounded-lg p-3">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-orange-100" />
                        <div className="text-sm text-orange-100">
                          Property information not available
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">
                      ${subscription.amount?.toFixed(2) || '0.00'}
                    </span>
                    <span className="text-orange-100 ml-2">
                      /{subscription.billingCycle === 'MONTHLY' ? 'month' : 'year'}
                    </span>
                  </div>
                </div>

                {/* Subscription Details */}
                <div className="p-6">
                  {/* Status Alert */}
                  {subscription.status === 'ACTIVE' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        <div className="flex-1">
                          <p className="text-sm text-green-800">
                            Next billing: {new Date(subscription.nextBillingDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {subscription.status === 'CANCELLED' && subscription.hasActiveAccess && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <AlertTriangle className="h-5 w-5 text-orange-600 mr-2" />
                        <div className="flex-1">
                          <p className="text-sm text-orange-800 font-medium">
                            Cancelled - Access until {new Date(subscription.endDate).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            ✓ No more charges will be taken
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {(subscription.status === 'EXPIRED' || (subscription.status === 'CANCELLED' && !subscription.hasActiveAccess)) && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <X className="h-5 w-5 text-red-600 mr-2" />
                        <div className="flex-1">
                          <p className="text-sm text-red-800 font-medium">
                            Expired on {new Date(subscription.endDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {subscription.status === 'PENDING' && (
                    <>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center">
                          <Clock className="h-5 w-5 text-orange-600 mr-2" />
                          <div className="flex-1">
                            <p className="text-sm text-orange-800 font-medium">
                              Payment pending - Complete payment to activate
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Voucher Section */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Have a voucher code?
                        </label>

                        {subscription.appliedVoucher ? (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                                <div>
                                  <p className="text-sm font-medium text-green-900">
                                    Voucher "{subscription.appliedVoucher.code}" applied
                                  </p>
                                  <p className="text-xs text-green-700">
                                    Discount: ${subscription.appliedVoucher.discountAmount.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveVoucher(subscription._id)}
                                disabled={applyingVoucher[subscription._id]}
                                className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                value={voucherCode[subscription._id] || ''}
                                onChange={(e) => setVoucherCode({
                                  ...voucherCode,
                                  [subscription._id]: e.target.value.toUpperCase()
                                })}
                                placeholder="Enter voucher code"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm uppercase"
                                disabled={applyingVoucher[subscription._id]}
                              />
                            </div>
                            <button
                              onClick={() => handleApplyVoucher(subscription._id)}
                              disabled={applyingVoucher[subscription._id] || !voucherCode[subscription._id]}
                              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {applyingVoucher[subscription._id] ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Applying...
                                </>
                              ) : (
                                <>
                                  <Percent className="h-4 w-4" />
                                  Apply
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Usage Stats */}
                  {subscription.currentUsage && subscription.status === 'ACTIVE' && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Monthly Usage</h4>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Inspections</span>
                            <span className="font-medium">
                              {subscription.currentUsage.inspectionsUsed || 0}/1
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-orange-600 h-2 rounded-full"
                              style={{
                                width: `${((subscription.currentUsage.inspectionsUsed || 0) / 1) * 100}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {subscription.status === 'ACTIVE' && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setShowCancelModal(true);
                          }}
                          className="flex items-center px-4 py-2 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors text-sm"
                        >
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Cancel
                        </button>
                        
                        {subscription.property && (
                          <button
                            onClick={() => navigate('/properties')}
                            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                          >
                            <Settings className="h-4 w-4 mr-2" />
                            Manage Property
                          </button>
                        )}
                      </>
                    )}

                    {subscription.status === 'CANCELLED' && subscription.hasActiveAccess && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setShowCancelModal(true);
                          }}
                          className="flex items-center px-4 py-2 border border-orange-300 rounded-lg text-orange-700 hover:bg-orange-50 transition-colors text-sm"
                        >
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Cancel Immediately
                        </button>
                        
                        {subscription.property && (
                          <button
                            onClick={() => navigate('/properties')}
                            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                          >
                            <Settings className="h-4 w-4 mr-2" />
                            Manage Property
                          </button>
                        )}
                      </>
                    )}

                    {subscription.status === 'PENDING' && (
                      <>
                        <button
                          onClick={() => handleRetrySubscriptionPayment(subscription)}
                          disabled={completingPayment}
                          className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          {completingPayment ? 'Processing...' : 'Make Payment'}
                        </button>

                        <button
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setShowCancelModal(true);
                          }}
                          className="flex items-center px-4 py-2 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors text-sm"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel Subscription
                        </button>
                        
                        {subscription.property && (
                          <button
                            onClick={() => navigate('/properties')}
                            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                          >
                            <Settings className="h-4 w-4 mr-2" />
                            Manage Property
                          </button>
                        )}
                      </>
                    )}

                    {(subscription.status === 'EXPIRED' || (subscription.status === 'CANCELLED' && !subscription.hasActiveAccess)) && (
                      <>
                        <button
                          onClick={() => navigate('/subscriptions')}
                          className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Renew Subscription
                        </button>
                        
                        {subscription.property && (
                          <button
                            onClick={() => navigate('/properties')}
                            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                          >
                            <Settings className="h-4 w-4 mr-2" />
                            Manage Property
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Cancellation Modal */}
      <AnimatePresence>
        {showCancelModal && selectedSubscription && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedSubscription.status === 'CANCELLED' ? 'Cancel Immediately' : 
                    `Cancel ${selectedSubscription.tierType ? 'Membership' : 'Subscription'}`}
                </h3>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  You are about to cancel your{' '}
                  {selectedSubscription.tierType ? (
                    <strong>{selectedSubscription.tierType}</strong>
                  ) : (
                    <strong>{selectedSubscription.propertyType}</strong>
                  )}{' '}
                  {selectedSubscription.tierType ? 'membership' : 'property subscription'}.
                </p>

                {selectedSubscription.status === 'PENDING' ? (
                  // For pending subscriptions/memberships, just cancel immediately
                  <button
                    onClick={() => handleCancelSubscription(true)}
                    disabled={cancelLoading}
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {cancelLoading ? 'Processing...' : 
                      `Cancel Pending ${selectedSubscription.tierType ? 'Membership' : 'Subscription'}`}
                  </button>
                ) : selectedSubscription.status === 'CANCELLED' ? (
                  <button
                    onClick={() => handleCancelSubscription(true)}
                    disabled={cancelLoading}
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {cancelLoading ? 'Processing...' : 'End Access Immediately'}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={() => handleCancelSubscription(false)}
                      disabled={cancelLoading}
                      className="w-full text-left p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <div className="font-medium text-gray-900">Cancel at Period End</div>
                      <div className="text-sm text-gray-600">
                        Keep access until {new Date(selectedSubscription.nextBillingDate).toLocaleDateString()}
                      </div>
                    </button>

                    <button
                      onClick={() => handleCancelSubscription(true)}
                      disabled={cancelLoading}
                      className="w-full text-left p-3 border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      <div className="font-medium text-red-900">Cancel Immediately</div>
                      <div className="text-sm text-red-600">
                        Lose access right away, no refund
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {cancelLoading && (
                <div className="text-center py-2">
                  <div className="inline-block w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-sm text-gray-600">Processing...</span>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change Plan Modal */}
      <AnimatePresence>
        {showChangePlanModal && selectedSubscription && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowChangePlanModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Change Property Type
                </h3>
                <button
                  onClick={() => setShowChangePlanModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-orange-900 mb-2">Current Subscription</h4>
                <p className="text-orange-800">
                  {selectedSubscription.propertyType} Property - {selectedSubscription.billingCycle} billing
                </p>
              </div>

              <div className="space-y-4">
                {['HDB', 'CONDO', 'LANDED'].map((type) => {
                  const PropertyIcon = getPropertyIcon(type);
                  const isCurrent = selectedSubscription.propertyType === type;

                  return (
                    <button
                      key={type}
                      onClick={() => {
                        if (!isCurrent) {
                          handleChangePlan(type);
                        }
                      }}
                      disabled={isCurrent || changingPlan}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        isCurrent
                          ? 'border-orange-500 bg-orange-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <PropertyIcon className="h-8 w-8 text-orange-600 mr-3" />
                        <div className="text-left flex-1">
                          <div className="font-semibold text-gray-900">{type} Property</div>
                          <div className="text-sm text-gray-600">
                            {isCurrent ? 'Current plan' : 'Switch to this type'}
                          </div>
                        </div>
                        {isCurrent && (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default SubscriptionDashboard;
